import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { logEvent } from '@/lib/analytics/firebase';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  createPredictionLeague,
  getMyPredictionStats,
  getPredictionLeaderboard,
  getPredictionLeagueBoard,
  getPredictionTally,
  getRecentWin,
  joinPredictionLeague,
  leavePredictionLeague,
  listMyPredictionLeagues,
  listMyPredictions,
  savePrediction,
} from '@/lib/data/provider';
import type { PredictionPick, Tables } from '@/lib/supabase/types';

/** The user's picks, keyed by fixture id. */
export function useMyPredictions() {
  const { userId } = useAuth();
  const query = useQuery({
    queryKey: ['predictions', 'mine', userId ?? 'anon'],
    queryFn: listMyPredictions,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
  const byFixture = useMemo(() => {
    const map = new Map<string, Tables<'match_predictions'>>();
    for (const row of query.data ?? []) {
      map.set(row.fixture_id, row);
    }
    return map;
  }, [query.data]);
  return { ...query, byFixture };
}

export function useSavePrediction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { fixtureId: string; pick: PredictionPick }) =>
      savePrediction(input.fixtureId, input.pick),
    onSuccess: (_data, input) => {
      logEvent('prediction_saved', { fixture_id: input.fixtureId, pick: input.pick });
      queryClient.invalidateQueries({ queryKey: ['predictions'] });
      queryClient.invalidateQueries({ queryKey: ['prediction-tally', input.fixtureId] });
    },
  });
}

/** Community vote tally for a fixture (counts only) - drives the "X% picked …" bar. */
export function usePredictionTally(fixtureId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['prediction-tally', fixtureId ?? 'none'],
    queryFn: () => getPredictionTally(fixtureId as string),
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured && enabled && Boolean(fixtureId),
  });
}

export function useMyPredictionStats() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['predictions', 'my-stats', userId ?? 'anon'],
    queryFn: getMyPredictionStats,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

/**
 * The user's single most-recent winning prediction across all three games (or
 * null). Drives the auto brag-card celebration on launch. Kept fresh on the
 * `user` cadence so a freshly-settled win is discovered shortly after the cron
 * settles it, without hammering the three-table read on every render.
 */
export function useRecentWin() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['predictions', 'recent-win', userId ?? 'anon'],
    queryFn: getRecentWin,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function usePredictionLeaderboard(enabled = true) {
  return useQuery({
    queryKey: ['predictions', 'leaderboard'],
    queryFn: () => getPredictionLeaderboard(),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && enabled,
  });
}

export function useMyPredictionLeagues() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['predictions', 'leagues', userId ?? 'anon'],
    queryFn: listMyPredictionLeagues,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function usePredictionLeagueBoard(leagueId: string | undefined) {
  return useQuery({
    queryKey: ['predictions', 'league-board', leagueId ?? 'none'],
    queryFn: () => getPredictionLeagueBoard(leagueId as string),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(leagueId),
  });
}

export function useCreatePredictionLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createPredictionLeague(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['predictions', 'leagues'] });
    },
  });
}

export function useJoinPredictionLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => joinPredictionLeague(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['predictions', 'leagues'] });
    },
  });
}

export function useLeavePredictionLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leagueId: string) => leavePredictionLeague(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['predictions', 'leagues'] });
    },
  });
}
